import { useCallback, useEffect, useState, type ReactNode } from "react";

import { useDialogs } from "@/components/shell/Dialogs";
import { dayMonthMaybeYear } from "@/lib/dates";
import {
  rateRequiredBy,
  saveStatsSettings,
  statsPace,
  type RateRequired,
  type StatsPace,
  // Aliased: the phone's row COMPONENT below wants the obvious name, and a component and a type
  // sharing one identifier is legal and reads as a mistake every time it is met.
  type ProjectionRow as PaceProjectionRow,
} from "@/lib/stats";
import { usePhone } from "@/lib/usePhone";

import { SkeletonRows, StatSkeleton } from "./Skeleton";
import { Panel, StatValue, Td, Th } from "./Panel";

/**
 * The four headings, hoisted for `History.tsx`'s reason: `PaceSkeleton` draws these same four
 * cards while `/pace` is in flight, so each title was written twice — and the goal panel's two
 * copies had already drifted, the skeleton saying `Pace toward the goal` where the loaded panel
 * named the actual figure.
 *
 * `goalTitle` is why that one is a function rather than a string: the number belongs in the
 * heading and only the payload knows it, so the skeleton says the heading without it rather than
 * inventing a goal it would then have to correct.
 */
const PANELS = {
  goal: { check: "pace-toward-the-goal", note: "通算平均 · 不足は累積" },
  projection: { title: "一定ペースでの予測", check: "sustained-rate-projection" },
  required: { title: "期日別の必要ペース", check: "rate-required-by-date" },
  supply: { title: "目標までの供給", note: "新規上限で消費", check: "pace-supply" },
} as const;

/** `維持ペース — 通算 4.0枚/日`, or just `維持ペース` before the goal has arrived. */
const goalTitle = (goal?: number) =>
  goal === undefined ? "維持ペース" : `維持ペース — 通算 ${goal}枚/日`;

/** The one unit a `StatValue` and its `StatSkeleton` both have to spell. */
const SUPPLY_UNIT = "枚 採取が必要";

/**
 * Pace — `8d`. Am I on track, and what would it take.
 *
 * **"Learned" means cards FIRST REVIEWED** — not added, and not graduated from learning. The three
 * give different numbers and the divergence is invisible unless pinned, so the definition is on
 * screen rather than only in the code.
 *
 * It meant *added* until 8 Aug 2026, when Alex corrected it: a card sitting untouched in the new
 * queue has not been learned. On the real collection that is 188 of Vocab's 8,885 cards. The change
 * also stops the projection mixing two quantities — its review-load column keys off the deck's
 * new-cards-per-day limit, and that limit governs how many new cards you are *shown*.
 *
 * The goal is a **lifetime average**, so the deficit is cumulative and the headline stays red for
 * years. That is the honest arithmetic of the goal Alex chose, not a number to soften.
 */
export function Pace({ deck }: { deck: string }) {
  const [data, setData] = useState<StatsPace | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dialogs = useDialogs();
  const isPhone = usePhone();

  const load = useCallback(() => {
    let live = true;
    void statsPace(deck)
      .then((found) => live && setData(found))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [deck]);

  useEffect(() => {
    setData(null);
    setError(null);
    return load();
  }, [load]);

  const editGoal = useCallback(async () => {
    if (!data) return;
    const answer = await dialogs.prompt({
      title: "1日の習得枚数",
      label: "目標",
      hint: "通算の平均なので不足は累積します — 継続的なペースが目標を上回ってはじめて減ります。",
      initial: String(data.settings.goalPerDay),
      submitLabel: "保存",
    });
    if (answer === null) return;
    const goal = Number(answer);
    if (!Number.isFinite(goal)) return;
    try {
      await saveStatsSettings({ goalPerDay: goal });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [data, dialogs, load]);

  if (error) return <p className="p-[26px] text-secondary text-accent-text">{error}</p>;
  if (!data) return <PaceSkeleton />;

  const behind = data.gap.cardsBehind;
  const trailing30 = data.rates.find((r) => r.id === "trailing-30")?.perDay ?? null;

  return (
    <div className="flex flex-col gap-[15px]">
      <Panel
        {...PANELS.goal}
        title={goalTitle(data.gap.goal)}
        gap={13}
        right={
          <button
            onClick={editGoal}
            className="rounded-[var(--r-2)] border border-line px-[9px] py-[3px] text-[14px]/[1.4] text-ink-2 hover:border-line-strong"
          >
            目標: {data.gap.goal}枚/日 · 変更
          </button>
        }
      >
        {/* The frames' arrangement: the deficit in its own column with a hairline beside it, and
            the five rates across the rest of one row. Stacked (the first pass) it read as two
            unrelated blocks, and the whole point is that the rates are what produced the deficit. */}
        <div className="flex flex-col gap-[13px] lg:flex-row lg:items-start lg:gap-[22px]">
          <div className="flex shrink-0 flex-col gap-[4px] lg:w-[214px] lg:border-r lg:border-line-soft lg:pr-[22px]">
            <Caption>{behind >= 0 ? "目標に対する遅れ" : "目標に対する貯金"}</Caption>
            <StatValue
              value={Math.abs(behind).toLocaleString()}
              size={52}
              tone={behind >= 0 ? "var(--accent-text)" : "var(--ink-3)"}
              note={`現時点の想定 ${data.gap.expectedByNow.toLocaleString()}枚 に対して保有 ${data.gap.cardsHeld.toLocaleString()}枚。`}
            />
          </div>

          {/* **Two columns at phone width, not one.** The first breakpoint here was `sm` (640px),
              which a 390px screen never reaches — so the five rates stacked one per row at ~75px
              each, spending 375px of an 844px viewport on five two-digit numbers before the
              projection they exist to be compared against was on screen at all. They are short,
              labelled and read in pairs, which is exactly what a two-column grid is for. */}
          <div className="grid min-w-0 flex-1 grid-cols-2 gap-[13px] sm:grid-cols-3 lg:grid-cols-5">
            {data.rates.map((rate) => {
              const today = rate.id === "today";
              const ahead = today && trailing30 !== null && rate.perDay > trailing30;
              return (
                <div key={rate.label} className="flex flex-col gap-[3px]">
                  <Caption>{rate.label}</Caption>
                  <span
                    className="text-[27px]/[1] font-semibold"
                    style={{ color: ahead ? "var(--ink-3)" : "var(--ink)" }}
                  >
                    {rate.perDay.toFixed(2)}
                  </span>
                  {/* Every rate beside its denominator — rule 2, and the reason a 6-card morning
                      cannot read as a trend. */}
                  <span className="text-[14px]/[1.4] text-ink-3">
                    {today && trailing30 !== null
                      ? `直近30日の${trailing30.toFixed(2)}に対して · n = 1日`
                      : `${rate.cards.toLocaleString()}枚`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <p className="border-t border-line-soft pt-[11px] text-[14px]/[1.5] text-ink-2">
          直近30日のペースでは差が{" "}
          <strong className="font-mono font-semibold text-ink">
            {data.gap.gapGrowthPerDay.toFixed(1)}枚/日
          </strong>{" "}
          ずつ広がり · 年間{Math.abs(data.gap.gapGrowthPerYear).toLocaleString()}枚ずつ
          {data.gap.gapGrowthPerYear >= 0 ? "遅れます" : "先行します"}。
        </p>
      </Panel>

      {/* At 16:9 the projection sits beside the two panels that interrogate it — the rate you
          would need for a date, and how long the deck can even feed a rate — which is the frame's
          arrangement and the order the questions are actually asked in. */}
      <div className="flex flex-col gap-[15px] 2xl:flex-row 2xl:items-start">
        {/* The frames' own widths as ratios — 1012 against 780 at 1920, not an even split. An
            even one gives the projection's four-column table less room than the two cards
            interrogating it, which inverts what the panel is for. */}
        <div className="min-w-0 2xl:flex-[1012_1_0%]">
        <Panel
          {...PANELS.projection}
          note={
            data.reviewsMeasured
              ? `復習コストは新規1枚あたり90日で${data.reviewsPerNewCard.toFixed(1)}回と実測`
              : `復習コストは新規1枚あたり${data.reviewsPerNewCard.toFixed(1)}回と仮定 — 実測するには履歴が少なすぎます`
          }
          gap={6}
        >
          {isPhone ? (
            <div className="flex flex-col">
              {data.projection.map((row) => (
                <ProjectionRow key={row.rate} row={row} goal={data.gap.goal} />
              ))}
            </div>
          ) : (
          <table className="w-full">
            <thead>
              <tr>
                <Th align="left">維持ペース</Th>
                <Th>通算到達 {data.gap.goal.toFixed(1)}</Th>
                <Th>要採取</Th>
                <Th>1日の復習負荷</Th>
              </tr>
            </thead>
            <tbody>
              {data.projection.map((row) => (
                <tr
                  key={row.rate}
                  className="border-t border-line-soft"
                  /* The current rate is the row every other row is compared against, found by
                     `kind` — a stable handle the server owns and no reader ever sees.
                     It was found by `note === "current trailing-30"`, matching on the row's own
                     PROSE, and the comment here used to warn that translating either side would
                     silently drop this highlight. It would have: `note` is Japanese now. Four
                     sites had the bug (this one twice, the phone row, and the trailing-30 lookup
                     that feeds every projection) plus `api/stats.py`, where the SERVER read its
                     own English label back. Nothing would have gone red. */
                  data-check={row.kind === "current" ? "row.current-rate" : undefined}
                  style={
                    row.kind === "current"
                      ? { background: "var(--accent-tint-2)" }
                      : undefined
                  }
                >
                  <td className="py-[7px] pr-[10px] text-[14px]/[19.6px]">
                    <span className="font-mono font-semibold text-ink">{row.rate.toFixed(1)}</span>
                    <span className="text-ink-3"> 枚/日</span>
                    {/* `row.note` is the SERVER's — `直近30日の実績`, `デッキの新規上限` — and is
                        already Japanese. It is rendered, never matched on; see `row.kind` below. */}
                    {row.note && <span className="text-ink-3"> — {row.note}</span>}
                    {row.needsSettingsChange && (
                      <span className="text-ink-4"> · 設定の変更が必要</span>
                    )}
                  </td>
                  {/* `なし` is a real answer and is rendered as the word. A blank would read as
                      missing data, where this is the arithmetic being definite. */}
                  <Td color={row.reachesGoalOn ? "var(--ink)" : "var(--accent-text)"}>
                    {row.reachesGoalOn
                      ? dayMonthMaybeYear(new Date(`${row.reachesGoalOn}T00:00:00`))
                      : "なし"}
                  </Td>
                  {/* **要採取, not the demand it replaced.** They differ by a constant — the
                      cards already in the deck — so drawing both put two figures a few hundred
                      apart on every row, and only one of them is a thing you can act on. The
                      demand is still printed, once, on the supply panel.

                      The fall down this column IS the incentive (Alex, 22 Aug 2026): a slower
                      rate does not merely arrive later, it needs MORE cards, because the target
                      grows while you fill it. */}
                  <Td tone="muted">
                    {row.cardsToMine === null ? "—" : row.cardsToMine.toLocaleString()}
                    {row.minePerDay !== null && (
                      <span className="block text-ink-4">{row.minePerDay.toFixed(1)}枚/日</span>
                    )}
                  </Td>
                  <Td tone="muted">
                    {row.addedReviewLoad >= 0 ? "+" : ""}
                    {row.addedReviewLoad.toFixed(0)} → {row.totalReviewLoad.toFixed(0)}回/日
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
          <p className="text-[14px]/[1.5] text-ink-3">
            デッキの新規上限 {data.newCardLimit}枚/日 · 現在の負荷{" "}
            {data.currentReviewLoad.toFixed(0)}回/日。
          </p>
        </Panel>
        </div>

        <div className="flex min-w-0 flex-col gap-[15px] lg:flex-row 2xl:flex-[780_1_0%] 2xl:flex-col">
          <div className="min-w-0 flex-1">
            <RequiredPanel deck={deck} today={data.today} goal={data.gap.goal} />
          </div>
          <div className="shrink-0 lg:w-[366px] 2xl:w-auto">
            <SupplyPanel supply={data.supply} goal={data.gap.goal} behind={data.gap.cardsBehind} />
          </div>
        </div>
      </div>

    </div>
  );
}

/**
 * One projection row on a phone: the rate, then the three answers on a line under it.
 *
 * The desktop's four columns cannot survive 390px — measured, the header alone wrapped to three
 * lines (`DAILY REVIEW LOAD`), every date broke across two (`1 Jan` / `2028`) and the current-rate
 * label took three (`0.9 /day —` / `current trailing-` / `30`). A table whose every cell wraps is
 * not a table: nothing lines up, so the column comparison it exists for is gone and only the cost
 * remains.
 *
 * **The rate leads because it is what you are choosing between**, and the three answers follow in
 * the column order the desktop keeps — when it reaches the goal, what that costs in cards, what it
 * costs in daily reviews — so the two forms read the same way round. `·` separates them rather than
 * a second grid: three short values on one line is a sentence, and a two-column grid here would
 * reintroduce the wrapping in miniature.
 *
 * The current-rate row keeps its ground, and `data-check` stays on it — `check-stats.mjs` finds
 * that row by the attribute rather than by shape, so the same assertion holds at both widths.
 */
function ProjectionRow({ row, goal }: { row: PaceProjectionRow; goal: number }) {
  const current = row.kind === "current";
  return (
    <div
      className="flex flex-col gap-[3px] border-t border-line-soft px-[2px] py-[9px]"
      data-check={current ? "row.current-rate" : undefined}
      style={current ? { background: "var(--accent-tint-2)" } : undefined}
    >
      <span className="text-[14px]/[1.4]">
        <span className="font-mono font-semibold text-ink">{row.rate.toFixed(1)}</span>
        <span className="text-ink-3"> 枚/日</span>
        {row.note && <span className="text-ink-3"> — {row.note}</span>}
        {row.needsSettingsChange && <span className="text-ink-4"> · 設定の変更が必要</span>}
      </span>
      <span className="font-mono text-[14px]/[1.5] text-ink-3">
        <span style={{ color: row.reachesGoalOn ? "var(--ink-2)" : "var(--accent-text)" }}>
          {row.reachesGoalOn
            ? `${dayMonthMaybeYear(new Date(`${row.reachesGoalOn}T00:00:00`))}に${goal.toFixed(1)}到達`
            : `${goal.toFixed(1)}に到達しない`}
        </span>
        {" · "}
        {/* 要採取 takes the accent here where the desktop leaves it muted: on one folded line it
            is the figure the reader is choosing between, and the date beside it is already
            spoken for by its own colour.

            **Truthiness, not a null check** — the same test `RequiredPanel` makes, and for the
            supply panel's stated reason: red marks what wants you, and a shortfall of 0 wants
            nothing. Against `=== null` a deck holding more than the demand painted `0枚採取` in
            the loudest colour on the surface, on every row, in the one state where there is
            nothing to do. */}
        <span style={{ color: row.cardsToMine ? "var(--accent-text)" : undefined }}>
          {row.cardsToMine === null ? "—" : `${row.cardsToMine.toLocaleString()}枚採取`}
        </span>
        {" · "}
        {row.addedReviewLoad >= 0 ? "+" : ""}
        {row.addedReviewLoad.toFixed(0)} → {row.totalReviewLoad.toFixed(0)}回/日
      </span>
    </div>
  );
}

/**
 * 目標までの供給 — is there anything left in the deck to learn between here and the goal (日本-461).
 *
 * Every other panel on Pace asks what it would take to *reach* the goal. This one asks whether the
 * deck can feed the attempt, and on the real collection the answer is no by a wide margin: ~205
 * unseen cards against a goal ~1,000 days out. Nothing else on the surface says so.
 *
 * **It absorbed 新規カードの残り rather than sitting beside it.** That panel divided the same
 * `unseen` by the same limit; its 20日 is the `尽きるまで` figure here, and a runway drawn twice —
 * once with the goal in the question and once without — is two numbers a reader has to reconcile.
 *
 * **The deck drains at the deck's new/day LIMIT, not at the trailing-30 rate** (Alex, 22 Aug 2026).
 * The limit is the rate he is aiming to hit every day. It is stated on the panel's face, because
 * the tab's own *current* rate is a different number on the same screen — 10.5 against a limit of
 * 10 — and two plausible readings of one figure is worse than the figure being absent.
 *
 * Four states, all real, none of them clamped into another — **asked in this order**, for the
 * reason written over the branch itself:
 *
 * - **met** (`behind <= 0`) — the panel reverts to being the runway panel it absorbed. The only
 *   state whose headline is not the shortfall.
 * - **never** (`cardsToMine === null`, the rate is at or below the goal *and* the goal is not yet
 *   met) — the word なし, the table's own convention. The track is hatched rather than empty: an
 *   empty bar reads as *no stock*, which is a different and false claim. 尽きるまで still answers,
 *   because a runway needs no goal.
 * - **enough** (`cardsToMine === 0`) — **no red anywhere**. `tokens.css`: red marks what wants you,
 *   and a deck with the stock to reach the goal wants nothing.
 * - otherwise the shortfall, in `--accent-text`.
 */
function SupplyPanel({
  supply,
  goal,
  behind,
}: {
  supply: StatsPace["supply"];
  goal: number;
  behind: number;
}) {
  // **`met` is decided FIRST, and the order is the correctness.** `days_to_goal` answers `None`
  // on `rate <= goal` *before* it looks at the deficit, so a deck whose new-card limit sits at or
  // below the goal answers `None` even when the goal is already met — and asked in the other order
  // this panel then told a reader who is comfortably ahead that the goal is unreachable and no
  // amount of mining would help. The deficit is a fact about what you have done; the rate is a
  // fact about what you are doing, and only the first can settle whether the goal is met. Found by
  // review, 22 Aug 2026.
  const met = behind <= 0;
  const never = !met && supply.cardsToMine === null;
  const enough = !met && !never && supply.cardsToMine === 0;

  // The bar's denominator is the demand, so the one state with no demand (met) is also the one
  // where the stock trivially covers it — a full bar, rather than a made-up horizon to divide by.
  const filled = supply.demand ? Math.min(1, supply.unseen / supply.demand) : 1;

  // Red on the runway means *the deck runs dry before you get there*, which is a comparison and
  // not a threshold — so it cannot fire in the two states where there is no "there".
  const dryFirst =
    supply.runwayDays !== null &&
    supply.daysToGoal !== null &&
    supply.daysToGoal > 0 &&
    supply.runwayDays < supply.daysToGoal;

  const headline = never
    ? { value: "なし", unit: "必要枚数は決まりません", tone: "var(--accent-text)" }
    : met
      ? {
          value: supply.runwayDays === null ? "—" : supply.runwayDays.toLocaleString(),
          unit: "日 で在庫が尽きます",
          tone: "var(--ink)",
        }
      : enough
        ? { value: "足りています", unit: undefined, tone: "var(--ink)" }
        : {
            value: (supply.cardsToMine ?? 0).toLocaleString(),
            unit: SUPPLY_UNIT,
            tone: "var(--accent-text)",
          };

  const note = never
    ? `新規上限 ${supply.drainPerDay}枚/日 では通算 ${goal}枚/日 に到達しないため、どれだけ採取しても足りません。`
    : met
      ? "目標は達成済みです。あとは在庫を切らさないだけ。"
      : enough
        ? `在庫 ${supply.unseen.toLocaleString()}枚 は到達までの ${(supply.demand ?? 0).toLocaleString()}枚 を上回ります。`
        : `到達までに ${(supply.demand ?? 0).toLocaleString()}枚 を学習します。在庫は ${supply.unseen.toLocaleString()}枚。`;

  // Lifted out of the label rather than nested inside its template: a template inside a template
  // is what the chrome extractor cannot read, and it reached `docs/CHROME.md` truncated mid-string.
  const needed = never ? "なし" : `${(supply.demand ?? 0).toLocaleString()}枚`;

  return (
    <Panel {...PANELS.supply} gap={11}>
      {/* Column at 366, row at 1920 — the goal panel's own shape, and the same two partitions the
          surface already draws. The deficit block takes the hairline in the wide form only, where
          there is something beside it for the rule to separate. */}
      <div className="flex flex-col gap-[11px] 2xl:flex-row 2xl:items-start 2xl:gap-[22px]">
        <div className="flex flex-col gap-[4px] 2xl:w-[250px] 2xl:shrink-0 2xl:border-r 2xl:border-line-soft 2xl:pr-[22px]">
          <StatValue value={headline.value} unit={headline.unit} note={note} size={46} tone={headline.tone} />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-[12px]">
          <div className="flex flex-col gap-[5px]">
            <div
              className="flex h-[8px] overflow-hidden rounded-[4px]"
              style={{
                background: never
                  ? // Hatched, not empty. See the component note: an empty track claims the deck
                    // is out of stock, where what is actually unknown is how much it would need.
                    "repeating-linear-gradient(135deg, var(--line-soft) 0 6px, var(--line) 6px 12px)"
                  : "var(--line)",
              }}
            >
              {!never && (
                <span
                  style={{
                    width: `${filled * 100}%`,
                    minWidth: filled > 0 ? 3 : 0,
                    // **A full bar is neutral; only a short one is red.** Both healthy states
                    // fill the track completely — you hold everything the goal asks for — and at
                    // `--accent` that is the loudest thing on the panel, an alarm for the one
                    // state where nothing is wrong. Seen on the real screen rather than in the
                    // mock, where that state happened to be drawn part-filled.
                    background: enough || met ? "var(--ink-4)" : "var(--accent)",
                  }}
                />
              )}
            </div>
            <div className="flex justify-between">
              {/* One template literal each rather than text split around an expression: the
                  chrome record inventories what the source spells, so a label broken into
                  fragments reaches `docs/CHROME.md` as 在庫 / 枚 — pieces nobody can sign off. */}
              <span className="text-[14px]/[1.4] text-ink-3">
                {`在庫 ${supply.unseen.toLocaleString()}枚`}
              </span>
              <span className="text-[14px]/[1.4] text-ink-4">
                {`必要 ${needed}`}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-[13px] border-t border-line-soft pt-[11px] 2xl:grid-cols-3 2xl:border-t-0 2xl:pt-0">
            <Figure
              label="必要な採取ペース"
              value={
                supply.minePerDay === null ? "—" : `${supply.minePerDay.toFixed(1)}枚/日`
              }
              tone={supply.minePerDay === null ? "var(--ink-3)" : undefined}
            />
            <Figure
              label="尽きるまで"
              value={supply.runwayDays === null ? "—" : `${supply.runwayDays.toLocaleString()}日`}
              tone={dryFirst ? "var(--accent-text)" : undefined}
            />
            {/* Only where there is room for it. At 366 the table below carries a date per row, so
                a third figure here would be the one thing on the card said twice. */}
            <div className="hidden 2xl:block">
              <Figure
                label="到達まで"
                value={
                  supply.daysToGoal === null ? "なし" : `${supply.daysToGoal.toLocaleString()}日`
                }
              />
            </div>
          </div>
        </div>
      </div>

      <span className="text-[14px]/[1.5] text-ink-3">
        {`未学習${supply.unseen.toLocaleString()}枚・新規${supply.drainPerDay}枚/日で。`}
      </span>
    </Panel>
  );
}

/** The frames' small-caps column label: `600 10px`, uppercase, `--ink-3`. */
function Caption({ children }: { children: ReactNode }) {
  return (
    <span className="text-[14px]/[19.6px] font-semibold uppercase tracking-[.5px] text-ink-3">
      {children}
    </span>
  );
}

/**
 * The projection table read backwards — pick a date, get the rate.
 *
 * Alex asked the question this way round: not *when do I get there* but *how many would I have to
 * do*. Its own request, because the date is the reader's and the answer re-asks as it changes.
 */
function RequiredPanel({ deck, today, goal }: { deck: string; today: string; goal: number }) {
  const [by, setBy] = useState(() => `${Number(today.slice(0, 4)) + 4}-01-01`);
  const [answer, setAnswer] = useState<RateRequired | null>(null);

  useEffect(() => {
    let live = true;
    void rateRequiredBy(deck, by)
      .then((found) => live && setAnswer(found))
      .catch(() => live && setAnswer(null));
    return () => {
      live = false;
    };
  }, [deck, by]);

  return (
    <Panel {...PANELS.required} gap={12}>
      {/* One sentence with the controls in it, as the frames draw it — the question reads as a
          question rather than as a form above an answer. The Japanese keeps the same shape: the
          lead-in names the goal, the date input sits in the middle of the sentence, and the verb
          arrives after the answer, which is where Japanese wants it anyway. */}
      <div className="flex flex-wrap items-center gap-[10px] text-[14px]/[1.5] text-ink-2">
        通算 {goal.toFixed(1)}枚/日 の到達時期
        <input
          type="date"
          value={by}
          onChange={(e) => setBy(e.target.value)}
          className="rounded-[var(--r-2)] border border-line px-[7px] py-[3px] font-mono text-[14px]/[1.4] text-ink"
        />
        {answer?.rate == null ? (
          <span className="text-ink-4">— 未来の日付を選んでください。</span>
        ) : (
          <>
            必要
            <span className="text-[28px]/[1] font-semibold" style={{ color: "var(--accent-hover)" }}>
              {answer.rate.toFixed(1)}
            </span>
            枚/日 の維持
          </>
        )}
      </div>

      {/* Two up at phone width, for the reason the rate strip above is: `sm` is 640px, so these
          four stacked into four full rows on the one screen where the panel above them is already
          long. */}
      {answer?.rate != null && (
        <div className="grid grid-cols-2 gap-[13px] border-t border-line-soft pt-[12px] sm:grid-cols-4">
          <Figure
            label="直近30日の倍率"
            value={
              answer.multipleOfCurrent === null ? "—" : `${answer.multipleOfCurrent.toFixed(1)}×`
            }
          />
          <Figure
            label="デッキ上限超え"
            value={
              answer.aboveDeckLimit === null
                ? "—"
                : `${answer.aboveDeckLimit > 0 ? "+" : ""}${answer.aboveDeckLimit.toFixed(1)}枚/日`
            }
            tone={
              answer.aboveDeckLimit !== null && answer.aboveDeckLimit > 0
                ? "var(--accent-text)"
                : undefined
            }
          />
          <Figure
            label="増える復習負荷"
            value={
              answer.addedReviewLoad === null
                ? "—"
                : `${answer.addedReviewLoad > 0 ? "+" : ""}${answer.addedReviewLoad.toFixed(0)}回/日`
            }
          />
          {/* The same swap the table makes, for the same reason — this panel asks the projection
              backwards, so its fourth figure has to be the same quantity the column carries. */}
          <Figure
            label="要採取"
            value={answer.cardsToMine?.toLocaleString() ?? "—"}
            tone={answer.cardsToMine ? "var(--accent-text)" : undefined}
          />
        </div>
      )}
    </Panel>
  );
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex flex-col gap-[3px]">
      <Caption>{label}</Caption>
      <span
        className="font-mono text-[17px]/[1.2] font-semibold"
        style={{ color: tone ?? "var(--ink)" }}
      >
        {value}
      </span>
    </div>
  );
}


/** Pace before its payload — the goal card, the projection table, and the two panels beside it. */
function PaceSkeleton() {
  return (
    <div className="flex flex-col gap-[15px]">
      {/* No goal figure in the heading yet — `goalTitle()` says so rather than guessing one, since
          a number that then corrects itself is worse than a heading that arrives complete. */}
      <Panel {...PANELS.goal} title={goalTitle()} gap={10}>
        <div className="flex flex-wrap gap-[26px]">
          <StatSkeleton note="目標に対する遅れ" width={132} />
          <StatSkeleton note="通算のペース" width={96} />
          <StatSkeleton note="直近30日" width={96} />
        </div>
      </Panel>
      <div className="flex flex-col gap-[15px] 2xl:flex-row 2xl:items-start">
        <div className="min-w-0 2xl:flex-[1012_1_0%]">
          <Panel {...PANELS.projection} gap={6}>
            <SkeletonRows rows={6} height={16} gap={18} />
          </Panel>
        </div>
        <div className="flex min-w-0 flex-col gap-[15px] lg:flex-row 2xl:flex-[780_1_0%] 2xl:flex-col">
          <div className="min-w-0 flex-1">
            <Panel {...PANELS.required} gap={8}>
              <SkeletonRows rows={3} height={14} gap={16} />
            </Panel>
          </div>
          <div className="shrink-0 lg:w-[366px] 2xl:w-auto">
            <Panel {...PANELS.supply} gap={11}>
              {/* The panel's own shape, bar included, so the row does not reflow when it lands. */}
              <StatSkeleton note={SUPPLY_UNIT} width={132} />
              <div className="h-[8px] rounded-[4px] bg-line" />
              <SkeletonRows rows={1} height={17} gap={13} />
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
}
